import React from "react";
import { createRoot } from "react-dom/client";
import { YdsThemeProvider } from "@yleisradio/yds-components-react";
import { StyleSheetManager } from "styled-components";

import "./js/helpers/IntersectionObserver";

import { PreviewContainer } from "./js/components/PreviewContainer";
import { UiSizeResolver } from "./js/components/UiSizeResolver";
import { EmbedParametersContext } from "./js/contexts/embedParametersContext";
import { getPlayerEmbedParams } from "./js/helpers/embedParameters";
import { shouldForwardProp } from "./js/helpers/styledComponents";
import { PlayerStoreProvider } from "./js/store/PlayerStoreProvider";
import GlobalStyles from "./js/styles/global";
import { Player, PlayerSdkRender } from "./js/types/common";

const previewElements: HTMLElement[] = Array.from(
  document.querySelectorAll<HTMLElement>(".yle_areena_player")
);

export const renderPlayer: PlayerSdkRender = ({
  element,
  onPlayerReady,
  props
}) => {
  const embedParams = getPlayerEmbedParams(element, props);

  const previewContainerWrapper = document.createElement("div");
  element.appendChild(previewContainerWrapper);

  return new Promise<Player>(resolve => {
    const playerApiResolver = (playerPublicApi: Player) => {
      resolve(playerPublicApi);
      if (onPlayerReady) {
        if (embedParams.yleAudioPlayer) {
          throw Error(
            "YleAudioPlayer does not support onPlayerReady, use the Promise returned by render()."
          );
        } else {
          console.info(
            "onPlayerReady is deprecated and may be removed at some point. Use the Promise returned by render()."
          );
          onPlayerReady(playerPublicApi); // for backwards compatibility
        }
      }
    };

    const root = createRoot(previewContainerWrapper);

    root.render(
      <StyleSheetManager
        target={embedParams.styleSheetManagerTarget}
        shouldForwardProp={shouldForwardProp}>
        <EmbedParametersContext.Provider value={embedParams}>
          <GlobalStyles>
            <YdsThemeProvider>
              <PlayerStoreProvider
                embedParams={embedParams}
                initialState={{ playerElement: element }}>
                <UiSizeResolver playerElement={element}>
                  <PreviewContainer
                    onPlayerExternalApiReady={playerApiResolver}
                  />
                </UiSizeResolver>
              </PlayerStoreProvider>
            </YdsThemeProvider>
          </GlobalStyles>
        </EmbedParametersContext.Provider>
      </StyleSheetManager>
    );
  });
};

export const embedPlayerElements = () =>
  previewElements.forEach(element => {
    if (!element.getAttribute("data-isEmbedded")) {
      element.setAttribute("data-isEmbedded", "true");
      renderPlayer({ element }).catch(e =>
        console.error("Rendering player failed:", e)
      );
    }
  });
